import {useMemo, useState, useEffect} from 'react'
import {useTable, usePagination} from 'react-table'
import {useIntl, FormattedMessage} from 'react-intl'
import {KTIcon} from '../../../_metronic/helpers'
import usePostApi from '../custom_hooks/usePostApi'
import {PageTitle} from '../../../_metronic/layout/core'
import TeamMembersKnowMore from '../know_more_modals/TeamMembersKmMain'
import TeamMembersKM from '../know_more_modals/team_members_KM/TeamMembers'
import 'react-datepicker/dist/react-datepicker.css'
import DateFormatter from '../DateFormatter'
import PropTypes from 'prop-types'
import {Pagination} from 'react-bootstrap'
import {UseToast} from '../useToast'
import {useAuth} from '../auth'
import {Signature} from 'lucide-react'
import {useThemeMode} from '../../../_metronic/partials'
import {LightOutlineButton} from '../../ui/LightOutlineButton'

const DataTable = ({
  columns,
  data,
  isLoading,
  fetchData,
  refresh,
  signedDocs,
  currentStatus,
  setCurrentStatus,
}) => {
  const [showKM, setShowKM] = useState(false)
  const intl = useIntl()
  const PendingReqs = [
    {
      title: 'My Account',
      path: `/my-pods`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: ``,
      path: `/my-pods`,
      isSeparator: true,
      isActive: false,
    },
    {
      title: `Pending Requests`,
      path: `/pending-requests`,
      isSeparator: false,
      isActive: false,
    },
  ]

  const [inputVal, setInputVal] = useState('')

  const filteredData = useMemo(() => {
    let data1 = currentStatus === 'signed' || currentStatus === 'mysigned' ? signedDocs : data
    return data1
      ? data1.filter((each) => {
          const docTitle = each?.doc_title

          const docTitleMatches = docTitle?.toLowerCase()?.includes(inputVal?.toLowerCase())
          const businessMatches = each.pod_business_name
            ?.toLowerCase()
            ?.includes(inputVal?.toLowerCase())
          const reqEmailMatches = each.requested_by_email
            ?.toLowerCase()
            ?.includes(inputVal?.toLowerCase())
          const reqNameMatches = each.requested_by_name
            ?.toLowerCase()
            ?.includes(inputVal?.toLowerCase())
          let statusMatches
          if (currentStatus === 'rejected') {
            statusMatches = each.doc_status === 'XR'
          } else if (currentStatus === 'pending') {
            statusMatches = each.doc_status !== 'XR'
          } else if (currentStatus === 'signed') {
            statusMatches = each.doc_status === 'C'
          } else if (currentStatus === 'mysigned') {
            statusMatches = each.doc_status !== 'C'
          } else {
            statusMatches = true
          }

          return (
            (docTitleMatches || businessMatches || reqEmailMatches || reqNameMatches) &&
            statusMatches
          )
        })
      : []
  }, [inputVal, data, currentStatus, signedDocs])

  const tableInstance = useTable({columns, data: filteredData}, usePagination)
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    state: {pageIndex, pageSize},
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageCount,
    gotoPage,
    setPageSize,
  } = tableInstance

  const pageOptions = [10, 20, 30, 40, 50]

  const handleDifferentViews = () => {
    if ((!isLoading.pendingRej || !isLoading.signed) && refresh === false) {
      return (
        <div className='card h-250px'>
          <div className='m-auto d-flex flex-column align-items-center'>
            <div className='spinner-border spinner-primary mr-15'></div>
          </div>
        </div>
      )
    } else if (data && data.length > 0) {
      return (
        <>
          <div className='d-flex flex-wrap flex-stack'>
            <div className='d-flex'>
              <h1 className='fs-3 fw-semibold'>Your Document Requests</h1>
            </div>
            <div className='d-flex'>
              <div className='m-auto d-flex align-items-center'>
                <button className='btn btn-sm btn-light fw-normal' onClick={() => setShowKM(true)}>
                  <i className='ki-duotone ki-information-2 fs-3'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                    <span className='path3'></span>
                  </i>
                  Know More
                </button>
              </div>
            </div>
          </div>
          <p className='fs-6 text-gray-600 mb-4'>
            This page displays all your document requests — whether they're pending your signature,
            already signed by you, or rejected. Stay updated on the status of each request and
            manage them efficiently from one place.
          </p>

          {refresh ? (
            <div className='card h-250px'>
              <div className='m-auto d-flex flex-column align-items-center'>
                <div className='spinner-border spinner-primary mr-15'></div>
              </div>
            </div>
          ) : (
            <>
              <div className='card p-5 mb-5'>
                <div className='d-flex flex-column flex-md-row justify-content-md-between border-0 px-0 mb-3'>
                  <ul className='nav d-flex align-items-center'>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-normal px-4 me-1 fs-6 ${
                          currentStatus === 'pending' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('pending')}
                      >
                        Pending
                      </span>
                    </li>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-success fw-normal px-4 me-1 fs-6 ${
                          currentStatus === 'signed' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('signed')}
                      >
                        {/* Signed */}
                        All Signed
                      </span>
                    </li>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-info fw-normal px-4 me-1 fs-6 ${
                          currentStatus === 'mysigned' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('mysigned')}
                      >
                        My Signed
                      </span>
                    </li>
                    <li className='nav-item'>
                      <span
                        className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-danger fw-normal px-4 me-1 fs-6 ${
                          currentStatus === 'rejected' ? 'active' : ''
                        }`}
                        data-bs-toggle='tab'
                        onClick={() => setCurrentStatus('rejected')}
                      >
                        Rejected
                      </span>
                    </li>
                  </ul>

                  <div className='d-flex flex-wrap gap-3'>
                    <div className='d-flex align-items-center position-relative'>
                      <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-3' />
                      <input
                        type='search'
                        id='kt_filter_search'
                        className='form-control form-control-white form-control-sm w-150px ps-9 fw-normal'
                        placeholder={intl.formatMessage({id: 'LABEL.SEARCH'})}
                        value={inputVal}
                        onChange={(e) => setInputVal(e.target.value)}
                        autoComplete='off'
                      />
                    </div>

                    <LightOutlineButton onClick={() => fetchData(true)}>
                      <i className='ki-solid ki-arrows-circle fs-3'></i>
                    </LightOutlineButton>
                  </div>
                </div>

                <div className='mb-0'>
                  <div className='overflow-auto'>
                    <table
                      className='table table-row-dashed table-row-gray-300 align-middle'
                      {...getTableProps()}
                    >
                      <thead>
                        <tr className='fw-semibold text-muted bg-light rounded'>
                          <th className='min-w-150px ps-3'>Document Name</th>
                          <th className='min-w-150px'>Business Name</th>
                          <th className='min-w-150px'>Requested From</th>
                          <th className='min-w-150px'>Requested Date</th>
                          <th className='min-w-150px'>Expiry</th>
                          <th className='min-w-100px'>Status</th>
                          <th className='min-w-150px text-center pe-3'>Actions</th>
                        </tr>
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {page.length > 0 ? (
                          page.map((row) => {
                            prepareRow(row)
                            return (
                              <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => (
                                  <td className='text-center' {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                  </td>
                                ))}
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td className='' colSpan={7}>
                              <div className='d-flex justify-content-center'>
                                <p className='text-dark default-cursor d-block fs-6 m-0 my-5 ps-3'>
                                  {inputVal === '' ? (
                                    intl.formatMessage({id: 'NO_RESULTS_FOUND_MSG'})
                                  ) : (
                                    <FormattedMessage
                                      id='MSG.NO_DATA_MATCH'
                                      values={{
                                        inputVal: <span className='text-primary'>{inputVal}</span>,
                                      }}
                                    />
                                  )}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {filteredData.length > 10 && (
                <Pagination>
                  <Pagination.First onClick={() => gotoPage(0)} disabled={!canPreviousPage} />
                  <Pagination.Prev onClick={previousPage} disabled={!canPreviousPage} />

                  {[...Array(pageCount)].map((_, i) => (
                    <Pagination.Item key={i} active={i === pageIndex} onClick={() => gotoPage(i)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}

                  <Pagination.Next onClick={nextPage} disabled={!canNextPage} />
                  <Pagination.Last
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                  />

                  <div className='d-flex align-items-center mx-5'>
                    <span className='me-2'>{intl.formatMessage({id: 'BTN.SHOW'})}</span>
                    <select
                      className='form-select form-select-solid fw-bold w-75px me-2'
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {pageOptions.map((option) => (
                        <option key={option} value={option} className='select-bg'>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </Pagination>
              )}
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          <h3 className='mb-4 fs-3 fw-semibold'>Stay on top of your Documents</h3>

          <p>
            This is your central hub for managing all your important documents. Whether you're
            sending documents out for signature or waiting to review and sign, you'll find their
            status here.
          </p>

          <div className='text-center mb-4'>
            <TeamMembersKM />

            <button className='btn btn-light-primary fw-normal' onClick={fetchData}>
              <i className='ki-solid ki-arrows-circle fs-3'></i>
              Reload
            </button>
          </div>
        </>
      )
    }
  }

  return (
    <>
      <TeamMembersKnowMore showModal={showKM} setShowModal={setShowKM} />

      <PageTitle breadcrumbs={PendingReqs}>Requests</PageTitle>
      {handleDifferentViews()}
    </>
  )
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func.isRequired,
      disableSortBy: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.shape({
    pendingRej: PropTypes.bool,
    signed: PropTypes.bool,
  }),
  fetchData: PropTypes.func.isRequired,
}

const PendingRequests = () => {
  const {mode} = useThemeMode()
  const {execute} = usePostApi()
  const {currentUser} = useAuth()
  const [reqsData, setReqsData] = useState([])
  const [signedDocs, setSignedDocs] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [loading, setLoading] = useState({
    pendingRej: false,
    signed: false,
  })
  const [currentStatus, setCurrentStatus] = useState('pending')

  const getNavigationBtn = (magic_key) => {
    // const url = `http://localhost:3013/recipient-sign?mg=${magic_key}`
    const url = `${process.env.REACT_APP_RECIPIENT_SIGN_APP}/recipient-sign?mg=${magic_key}`
    return (
      <a
        href={currentStatus !== 'pending' ? undefined : url}
        className={`btn btn-sm bg-${
          mode === 'dark' ? 'secondary' : 'white'
        } btn-outline border-primary btn-active-light-primary fs-6 text-primary ${
          currentStatus !== 'pending' ? 'disabled pointer-events-none opacity-50' : ''
        }`}
      >
        <Signature size={11} className='me-1' />
        Sign
      </a>
    )
  }

  const getDocuments = async (show = false) => {
    if (show) {
      setRefresh(true)
    }
    try {
      const url = '/pending/document/get'
      const formData = {
        email: currentUser.email,
      }

      const response = await execute(url, 'POST', formData)
      setReqsData(response.data)
      setLoading((prev) => ({...prev, pendingRej: true}))
    } catch (error) {
      console.error('Error:', error)
      setRefresh(false)
      if (show) {
        return UseToast(
          error.response?.data?.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'error'
        )
      }
    }
  }

  const fetchSignedDocs = async (show = false) => {
    if (show) {
      setRefresh(true)
    }
    try {
      const url = '/signed/document/get'
      const formData = {
        email: currentUser.email,
      }

      const response = await execute(url, 'POST', formData)
      setSignedDocs(response.data)
      // setRefresh(false)
      setLoading((prev) => ({...prev, signed: true}))

      // if (show) {
      //   return UseToast(
      //     response.status_message,
      //     {
      //       position: 'bottom-right',
      //       theme: 'colored',
      //       autoClose: true,
      //     },
      //     'success'
      //   )
      // }
    } catch (error) {
      console.error('Error:', error)
      setRefresh(false)
      if (show) {
        return UseToast(
          error.response?.data?.status_message,
          {
            position: 'bottom-right',
            theme: 'colored',
            autoClose: true,
          },
          'error'
        )
      }
    }
  }

  useEffect(() => {
    getDocuments()
    fetchSignedDocs()
  }, [])

  const reloadData = async () => {
    try {
      setLoading({pendingRej: false, signed: false})
      setRefresh(true)

      // Fetch both APIs in parallel to reduce waiting time
      await Promise.all([getDocuments(true), fetchSignedDocs(true)])

      UseToast(
        'POD documents fetched successfully',
        {
          position: 'bottom-right',
          theme: 'colored',
          autoClose: true,
        },
        'success'
      )
    } catch (error) {
      console.error('Error fetching documents:', error)
      UseToast(
        'Failed to fetch POD documents',
        {
          position: 'bottom-right',
          theme: 'colored',
          autoClose: true,
        },
        'error'
      )
    } finally {
      setRefresh(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({row}) => {
          return (
            <p className='ps-3 text-start text-dark fw-semibold text-hover-primary fs-5 default-cursor'>
              {row.original.doc_title}
            </p>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Business Name',
        accessor: 'business_name',
        Cell: ({row}) => {
          return (
            <p className='text-start text-dark fw-normal text-hover-primary fs-6 default-cursor'>
              {row.original.pod_business_name || '-'}
            </p>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Requested By',
        accessor: 'requested_by',
        Cell: ({row}) => {
          const {requested_by_name, requested_by_email} = row.original
          return (
            <div className='d-flex justify-content-start flex-column text-start'>
              <p className='text-dark fw-normal text-hover-primary mb-0 fs-6 default-cursor'>
                {requested_by_name || '-'}
              </p>
              <a href={`mailto:${requested_by_email}`} className='text-muted'>
                {requested_by_email}
              </a>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Requested Date',
        accessor: 'requested_date',
        Cell: ({row}) => {
          return (
            <p className='text-start text-dark fw-normal text-hover-primary fs-6 default-cursor'>
              {row.original.created_ts ? DateFormatter(row.original.created_ts) : '-'}
            </p>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Due Date',
        accessor: 'due_date',
        Cell: ({row}) => {
          return (
            <p className='text-start text-dark fw-normal text-hover-primary fs-6 default-cursor'>
              {row.original.expiry_ts ? DateFormatter(row.original.expiry_ts) : '-'}
            </p>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({row}) => {
          const {doc_status, doc_status_label} = row.original
          const statusClass =
            {
              S: 'badge badge-light-info',
              R: 'badge badge-light-danger',
              P: 'badge badge-light-info',
            }[doc_status] || 'badge badge-light-danger'

          return (
            <div className='text-start'>
              <span className={statusClass + ' fw-normal capitalize'}>{doc_status_label}</span>
            </div>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({row}) => {
          const {magic_key} = row.original
          return <div className='text-center'>{getNavigationBtn(magic_key)}</div>
        },
      },
    ],
    [currentStatus]
  )

  return (
    <DataTable
      columns={columns}
      data={reqsData || []}
      isLoading={loading}
      fetchData={reloadData}
      refresh={refresh}
      signedDocs={signedDocs}
      currentStatus={currentStatus}
      setCurrentStatus={setCurrentStatus}
    />
  )
}

export {PendingRequests}
